'use client';
'use strict';

var _tslib = require('../../../_virtual/_tslib.js');
var jsxRuntime = require('react/jsx-runtime');
var tailwindMerge = require('tailwind-merge');
var react = require('react');
require('../../DataDisplays/Avatar/Avatar.js');
var Typography = require('../../Base/Typography/Typography.js');
var Box = require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');
require('../../DataDisplays/Label/Label.js');
var SystemIcon = require('../../DataDisplays/SystemIcon/SystemIcon.js');
var ChoiceChip_styles = require('./ChoiceChip.styles.js');

/**
 * 선택 가능한 칩 컴포넌트입니다.
 *
 * @component
 * @param {Object} props - 컴포넌트 속성
 * @param {string} props.label - 칩에 표시될 텍스트 라벨
 * @param {boolean} [props.checked=false] - 체크박스의 선택 상태를 제어
 * @param {boolean} [props.error=false] - 에러 상태를 표시
 * @param {LargeSystemIconName} [props.leadingIcon] - 칩 좌측에 표시되는 큰 아이콘
 * @param {MediumSystemIconName} [props.trailingIcon] - 칩 우측에 표시되는 중간 크기 아이콘
 * @param {function} [props.onChange] - 체크박스 상태 변경 시 호출되는 이벤트 핸들러
 *
 * @example
 * // 기본 사용
 * <ChoiceChip
 *   label="선택 옵션"
 * />
 *
 * // 아이콘이 포함된 칩
 * <ChoiceChip
 *   label="아이콘 옵션"
 *   leadingIcon="home"
 *   trailingIcon="close"
 * />
 *
 * // 에러 상태의 칩
 * <ChoiceChip
 *   label="에러 상태"
 *   error={true}
 * />
 *
 * // 선택된 상태의 칩
 * <ChoiceChip
 *   label="선택된 옵션"
 *   checked={true}
 *   onChange={(e) => console.log('선택 상태:', e.target.checked)}
 * />
 */ const ChoiceChip = react.forwardRef((_a, ref)=>{
    var { label, checked = false, error = false, leadingIcon, trailingIcon, onChange } = _a, rest = _tslib.__rest(_a, [
        "label",
        "checked",
        "error",
        "leadingIcon",
        "trailingIcon",
        "onChange"
    ]);
    const [isChecked, setIsChecked] = react.useState(checked);
    react.useEffect(()=>{
        setIsChecked(checked);
    }, [
        checked
    ]);
    const handleChange = (e)=>{
        if (onChange) {
            onChange(e);
            setIsChecked(e.target.checked);
        }
    };
    return jsxRuntime.jsxs("label", {
        children: [
            jsxRuntime.jsx("input", Object.assign({
                type: "checkbox",
                checked: isChecked,
                className: "absolute h-0 w-0 opacity-0",
                onChange: handleChange
            }, rest, {
                ref: ref
            })),
            jsxRuntime.jsxs("div", {
                "data-testid": "design-system-choiceChip",
                className: tailwindMerge.twJoin('flex w-fit cursor-pointer select-none items-center gap-2 rounded-full', ChoiceChip_styles.generateBackgroundColor(isChecked, error), ChoiceChip_styles.generateBackgroundPadding(!!leadingIcon)),
                children: [
                    leadingIcon && jsxRuntime.jsx(Box, {
                        className: "flex size-8 items-center justify-center rounded-full bg-w-white p-0.5",
                        "data-testid": "design-system-choiceChip--leading-icon-container",
                        children: jsxRuntime.jsx(SystemIcon, {
                            name: leadingIcon,
                            className: ChoiceChip_styles.generateLeadingIconColor(isChecked, error)
                        })
                    }),
                    jsxRuntime.jsxs(Box, {
                        className: "flex items-center gap-1.5",
                        children: [
                            jsxRuntime.jsx(Typography, {
                                "data-testid": "design-system-choiceChip-label",
                                variant: "body1",
                                className: ChoiceChip_styles.generateLabelStyle(isChecked, error),
                                children: label
                            }),
                            trailingIcon && jsxRuntime.jsx(SystemIcon, {
                                name: trailingIcon,
                                className: ChoiceChip_styles.generateTrailingIconStyle(isChecked, error)
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
ChoiceChip.displayName = 'ChoiceChip';

module.exports = ChoiceChip;
